<template>
  <div class="brands-table">
    <Loading v-model:active="isLoading"/>

    <CCard>
      <CCardHeader>
        <strong>Brands Management</strong>
        <CButton color="primary" size="sm" class="float-right" @click="addNewBrand">
          <CIcon name="cil-plus" size="sm"/>
          Add New Brand
        </CButton>
      </CCardHeader>
      <CCardBody>
        <CDataTable
            :items="brands"
            :fields="fields"
            hover
            striped
            :loading="isLoading"
            responsive
        >
          <template #details="{ item }">
            <CCollapse :show="item._showDetails">
              <CCardBody>
                <Loading v-model:active="item._loadingModels"/>
                <template v-if="!item._loadingModels">
                  <div v-if="item.models && item.models.length > 0">
                    <h5 class="text-primary">Models for {{ item.title }}</h5>
                    <CDataTable
                        :items="item.models"
                        :fields="modelFields"
                        small
                        hover
                        striped
                    >
                      <template #actions="{ item: model }">
                        <td>
                          <CButton color="info" size="sm" class="mr-1" @click="editModel(item, model)">
                            Edit
                          </CButton>
                          <CButton color="danger" size="sm" @click="deleteModel(item, model)">
                            Delete
                          </CButton>
                        </td>
                      </template>
                    </CDataTable>
                  </div>
                  <div v-else class="text-center py-3">
                    <p class="text-muted mb-0">No models available for this brand.</p>
                  </div>
                </template>
                <CButton color="success" size="sm" class="mt-2" @click="addNewModel(item)">
                  Add New Model
                </CButton>
              </CCardBody>
            </CCollapse>
          </template>

          <template #actions="{ item }">
            <td>
              <CButton color="info" size="sm" class="mr-1" @click="editBrand(item)">
                Edit
              </CButton>
              <CButton color="danger" size="sm" @click="deleteBrand(item)">
                Delete
              </CButton>
            </td>
          </template>

          <template #show_details="{ item }">
            <td>
              <CButton color="primary" variant="outline" square size="sm" @click="toggleDetails(item)">
                {{ item._showDetails ? 'Hide' : 'Show' }} Models
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- Brand Edit Modal -->
    <CModal :show.sync="showBrandModal" :title="editingBrand.id ? 'Edit Brand' : 'Add New Brand'" size="lg">
      <CForm @submit.prevent="saveBrand">
        <CInput label="Title" v-model="editingBrand.title" required/>
        <div class="form-group">
          <CInput label="Logo" v-model="editingBrand.logo" required/>
        </div>
        <div class="form-group">
          <CInput label="Logo name" v-model="editingBrand.logo_name" required/>
        </div>
      </CForm>
      <template #footer>
        <CButton color="secondary" @click="showBrandModal = false">Cancel</CButton>
        <CButton color="primary" @click="saveBrand">Save</CButton>
      </template>
    </CModal>

    <!-- Model Edit Modal -->
    <CModal :show.sync="showModelModal" :title="editingModel.id ? 'Edit Model' : 'Add New Model'" size="lg">
      <CForm @submit.prevent="saveModel">
        <CInput label="Model Name" v-model="editingModel.name" required/>
      </CForm>
      <template #footer>
        <CButton color="secondary" @click="showModelModal = false">Cancel</CButton>
        <CButton color="primary" @click="saveModel">Save</CButton>
      </template>
    </CModal>
  <vue-toastr ref="toastr"></vue-toastr>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import {CIcon} from "@coreui/icons-vue";

export default {
  name: 'BrandsTable',
  components: {
    CIcon,
    Loading,
  },
  data() {
    return {
      fields: [
        { key: 'title', label: 'Brand Name', _classes: 'font-weight-bold' },
        { key: 'logo', label: 'Logo' },
        { key: 'logo_name', label: 'Logo name' },
        { key: 'show_details', label: 'Models' },
        { key: 'actions', label: 'Actions' }
      ],
      modelFields: [
        { key: 'name', label: 'Model Name' },
        { key: 'actions', label: 'Actions' }
      ],
      showBrandModal: false,
      showModelModal: false,
      editingBrand: {
        title: '',
        logo: null,
        logo_name: ''
      },
      editingModel: {},
      selectedBrand: null
    }
  },

  computed: {
    ...mapState({
      brands: state => state.brands.brands,
      isLoading: state => state.brands.isLoading
    })
  },

  created() {
    this.$store.dispatch('fetchBrands')
  },

  methods: {
    toggleDetails(item) {
      this.$store.dispatch('toggleDetails', item);
    },

    addNewBrand() {
      this.editingBrand = { title: '', logo: null, logo_name: '' }
      this.showBrandModal = true
    },

    editBrand(brand) {
      this.editingBrand = { ...brand }
      this.showBrandModal = true

    },

    saveBrand() {

      console.log(this.editingBrand);
      const brandData = {
        title: this.editingBrand.title,
        logo: this.editingBrand.logo,
        logo_name: this.editingBrand.logo_name,
      }

      this.$store.dispatch('saveBrand', {
        brand: this.editingBrand,
        brandData
      })
          .then(() => {
            this.$refs.toastr.s(
                this.editingBrand.id ? 'Brand updated successfully' : 'Brand created successfully'
            )
            this.showBrandModal = false
          })
          .catch(() =>  this.$refs.toastr.e('Error saving brand'))
    },

    deleteBrand(brand) {
      if (confirm('Are you sure you want to delete this brand?')) {
        this.$store.dispatch('deleteBrand', brand.id)
            .then(() =>  this.$refs.toastr.s('Brand deleted successfully'))
            .catch(() =>  this.$refs.toastr.e('Error deleting brand'))
      }
    },

    addNewModel(brand) {
      this.selectedBrand = brand
      this.editingModel = {}
      this.showModelModal = true
    },

    editModel(brand, model) {
      this.selectedBrand = brand
      this.editingModel = { ...model }
      this.showModelModal = true
    },

    saveModel() {
      const modelData = {
        name: this.editingModel.name,
        vehicle_brand_id: this.selectedBrand.id
      }

      this.$store.dispatch('saveModel', {
        brandId: this.selectedBrand.id,
        modelId: this.editingModel.id,
        modelData
      })
          .then(() => {
            this.$refs.toastr.s(
                this.editingModel.id ? 'Model updated successfully' : 'Model created successfully'
            )
            this.showModelModal = false
          })
          .catch(() => this.$refs.toastr.e('Error saving model'))
    },

    deleteModel(brand, model) {
      if (confirm('Are you sure you want to delete this model?')) {
        this.$store.dispatch('deleteModel', {
          brandId: brand.id,
          modelId: model.id
        })
            .then(() =>  this.$refs.toastr.s('Model deleted successfully'))
            .catch(() =>  this.$refs.toastr.e('Error deleting model'))
      }
    }
  }
}
</script>

<style scoped>
.brands-table .card-body {
  padding: 1rem;
}

.brands-table .table th {
  font-weight: bold;
}
</style>
